import React from 'react';
import { FiArrowLeft } from 'react-icons/fi';

// Two-font system:
// FONT_DISPLAY: Rajdhani — titles, section headers, buttons, tabs
// FONT_DATA: JetBrains Mono — values, stats, descriptions, lists
export const FONT_DISPLAY = "'Rajdhani', sans-serif";
export const FONT_DATA = "'JetBrains Mono', monospace";

// ===== PALETTE =====

export const PALETTE = {
  DARK: '#0f1b26',
  LIGHT: '#f5e8d1',
  TEAL: '#20a5a6',
  ORANGE: '#dd5639',
  DIM: '#4a5a6a',
  PANEL: '#1a2a38',
  GREEN: '#4ade80',
  RED: '#ef4444',
  YELLOW: '#fbbf24',
  BLUE: '#3b82f6',
  BORDER: '#2a3a48',
};

// ===== SLOT / RESOURCE COLORS =====

export const SLOT_COLORS: Record<string, string> = {
  ENGINE: '#fbbf24',
  WEAPON: '#ef4444',
  TOOL: '#4ade80',
  SHIELD: '#38bdf8',
  ENHANCER: '#60d4a0',
  UTILITY: '#a78bfa',
  DRONE: '#f97316',
};

export const RESOURCE_COLORS: Record<string, string> = {
  iron: '#a1a1aa',
  copper: '#f97316',
  titanium: '#38bdf8',
  platinum: '#a78bfa',
  water_ice: '#67e8f9',
  ammonia: '#86efac',
  methane: '#fbbf24',
};

// ===== TIER COLORS =====

export function getTierColor(tier: number): string {
  switch (tier) {
    case 1: return PALETTE.GREEN;
    case 2: return PALETTE.BLUE;
    case 3: return PALETTE.ORANGE;
    case 4: return PALETTE.RED;
    default: return PALETTE.LIGHT;
  }
}

// ===== LAYOUT STYLES =====

export const stationContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  backgroundColor: PALETTE.DARK,
  color: PALETTE.LIGHT,
  fontFamily: FONT_DATA,
};

export const stationHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 20px',
  borderBottom: `1px solid ${PALETTE.BORDER}`,
};

export const stationBackButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '6px 12px',
  backgroundColor: 'transparent',
  color: PALETTE.TEAL,
  border: 'none',
  cursor: 'pointer',
  fontFamily: FONT_DISPLAY,
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.5px',
};

export const stationTitleStyle: React.CSSProperties = {
  fontSize: '15px',
  fontWeight: 700,
  letterSpacing: '2px',
  textTransform: 'uppercase',
};

export const stationHintStyle: React.CSSProperties = {
  fontSize: '10px',
  fontWeight: 500,
  color: PALETTE.DIM,
  letterSpacing: '0.5px',
};

export const stationHeaderTabsStyle: React.CSSProperties = {
  display: 'flex',
  gap: '0',
};

export const stationHeaderTabStyle = (isActive: boolean): React.CSSProperties => ({
  padding: '8px 20px',
  backgroundColor: 'transparent',
  border: 'none',
  borderBottom: `2px solid ${isActive ? PALETTE.TEAL : 'transparent'}`,
  cursor: 'pointer',
  fontFamily: FONT_DISPLAY,
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '1px',
  textTransform: 'uppercase',
  color: isActive ? PALETTE.TEAL : PALETTE.DIM,
  transition: 'color 0.15s',
});

export const stationFooterStyle: React.CSSProperties = {
  padding: '12px 20px',
  fontSize: '9px',
  fontWeight: 500,
  color: PALETTE.DIM,
  borderTop: `1px solid ${PALETTE.BORDER}`,
  textAlign: 'center',
  letterSpacing: '0.5px',
};

export const stationContentStyle: React.CSSProperties = {
  flex: 1,
  overflow: 'auto',
  position: 'relative',
};

// ===== BUTTON STYLES =====

export const primaryBtnStyle = (disabled = false): React.CSSProperties => ({
  marginTop: 8,
  padding: '8px 16px',
  border: `1px solid ${PALETTE.TEAL}`,
  backgroundColor: disabled ? PALETTE.DIM : 'transparent',
  color: disabled ? PALETTE.DARK : PALETTE.TEAL,
  cursor: disabled ? 'not-allowed' : 'pointer',
  borderRadius: 4,
  fontSize: 11,
  fontFamily: FONT_DISPLAY,
  letterSpacing: '1px',
  fontWeight: 600,
  textTransform: 'uppercase' as const,
});

export const dangerBtnStyle = (disabled = false): React.CSSProperties => ({
  marginTop: 8,
  padding: '8px 16px',
  border: `1px solid ${PALETTE.RED}`,
  backgroundColor: 'transparent',
  color: PALETTE.RED,
  cursor: disabled ? 'not-allowed' : 'pointer',
  borderRadius: 4,
  fontSize: 11,
  fontFamily: FONT_DISPLAY,
  letterSpacing: '1px',
  fontWeight: 600,
  textTransform: 'uppercase' as const,
});

export const smallBtnStyle = (cancel = false): React.CSSProperties => ({
  padding: '4px 8px',
  border: `1px solid ${cancel ? PALETTE.DIM : PALETTE.TEAL}`,
  backgroundColor: 'transparent',
  color: cancel ? PALETTE.DIM : PALETTE.TEAL,
  cursor: 'pointer',
  borderRadius: 3,
  fontSize: 12,
  fontFamily: FONT_DISPLAY,
});

export const iconBtnStyle: React.CSSProperties = {
  padding: 4,
  border: 'none',
  background: 'transparent',
  color: PALETTE.DIM,
  cursor: 'pointer',
};

export const inputStyle: React.CSSProperties = {
  backgroundColor: PALETTE.DARK,
  color: PALETTE.LIGHT,
  border: `1px solid ${PALETTE.BORDER}`,
  borderRadius: 3,
  padding: '4px 8px',
  fontSize: 13,
  fontFamily: FONT_DISPLAY,
};

// ===== REUSABLE ATOM COMPONENTS =====

/** Standard station sub-page header with back button and optional title + hint */
export function StationHeader({
  onBack,
  title,
  hint,
  children,
}: {
  onBack: () => void;
  title?: string;
  hint?: string;
  children?: React.ReactNode;
}) {
  return (
    <div style={stationHeaderStyle}>
      <button style={stationBackButtonStyle} onClick={onBack}>
        <FiArrowLeft size={16} />
        <span>BACK</span>
      </button>
      {children ?? (
        <>
          {title && <span style={stationTitleStyle}>{title}</span>}
          {hint && <span style={stationHintStyle}>{hint}</span>}
        </>
      )}
    </div>
  );
}

/** Standard station footer */
export function StationFooter({ children }: { children: React.ReactNode }) {
  return <div style={stationFooterStyle}>{children}</div>;
}

/** Card container used in management/detail panels */
export function Card({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{
      backgroundColor: PALETTE.PANEL,
      border: `1px solid ${PALETTE.BORDER}`,
      borderRadius: 4,
      padding: 16,
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      ...style,
    }}>
      {children}
    </div>
  );
}

/** Key-value row inside a Card */
export function CardRow({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
      {children}
    </div>
  );
}

/** Label (left side of a CardRow) */
export function CardLabel({ children }: { children: React.ReactNode }) {
  return (
    <span style={{ color: PALETTE.DIM, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 11 }}>
      {children}
    </span>
  );
}

/** Value (right side of a CardRow) */
export function CardValue({ children }: { children: React.ReactNode }) {
  return <span style={{ color: PALETTE.LIGHT, fontSize: 13 }}>{children}</span>;
}

/** Section title inside a Card */
export function SectionTitle({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{ color: PALETTE.TEAL, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', ...style }}>
      {children}
    </div>
  );
}

/** Hint / secondary text */
export function Hint({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <div style={{ color: PALETTE.DIM, fontSize: 12, ...style }}>{children}</div>;
}

/** Error text */
export function ErrText({ children }: { children: React.ReactNode }) {
  return <div style={{ color: PALETTE.RED, fontSize: 12 }}>{children}</div>;
}

/** Loading indicator */
export function Loading() {
  return <Hint>Loading...</Hint>;
}

/** Scrollable body area used by simpler station sub-pages */
export function StationBody({ children, maxWidth = 720 }: { children: React.ReactNode; maxWidth?: number }) {
  return (
    <div style={{
      flex: 1,
      overflow: 'auto',
      padding: 20,
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      maxWidth,
      margin: '0 auto',
      width: '100%',
    }}>
      {children}
    </div>
  );
}
